import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, Copy, Check, Eye, Pencil } from "lucide-react";
import { GenerationPicker } from "@/components/admin/GenerationPicker";

interface GiftPage {
  id: string;
  slug: string;
  hero_name: string;
  event_name: string;
  track_title: string;
  audio_url: string;
  cover_url: string | null;
  user_id: string;
  created_at: string;
  accent_color: string;
  is_animation_enabled: boolean;
  views_count: number;
  gift_style: string;
  sender_name: string | null;
  custom_message: string | null;
}

const ACCENT_PRESETS = ["#7F5AF0", "#5F6FFF", "#D4AF37", "#E84393", "#00B894", "#FF6B6B", "#0984E3"];

const emptyGift = {
  slug: "", hero_name: "", event_name: "", track_title: "", audio_url: "",
  cover_url: "", user_id: "", accent_color: "#7F5AF0", is_animation_enabled: true,
  gift_style: "light", sender_name: "", custom_message: "",
};

export default function GiftPages() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<GiftPage> | null>(null);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [previewSlug, setPreviewSlug] = useState<string | null>(null);

  const { data: gifts = [], isLoading } = useQuery({
    queryKey: ["gift_pages"],
    queryFn: async () => {
      const { data, error } = await supabase.from("gift_pages").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as GiftPage[];
    },
  });

  const save = useMutation({
    mutationFn: async (g: Partial<GiftPage> & { _generation_id?: string }) => {
      const { _generation_id, id, created_at, views_count, ...rest } = g as any;
      const payload = {
        ...rest,
        cover_url: rest.cover_url || null,
        sender_name: rest.sender_name || null,
        custom_message: rest.custom_message || null,
      };
      if (isEditMode && id) {
        const { error } = await supabase.from("gift_pages").update(payload).eq("id", id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("gift_pages").insert([payload as any]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gift_pages"] });
      setOpen(false);
      setEditing(null);
      toast.success(isEditMode ? "Gift-страница обновлена" : "Gift-страница создана");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("gift_pages").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gift_pages"] });
      toast.success("Удалено");
    },
  });

  const GIFT_BASE_URL = "https://hitchef.ru";

  const copyLink = (slug: string, id: string) => {
    navigator.clipboard.writeText(`${GIFT_BASE_URL}/g/${slug}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openCreate = () => {
    setEditing({ ...emptyGift });
    setIsEditMode(false);
    setOpen(true);
  };

  const openEdit = (g: GiftPage) => {
    setEditing({ ...g });
    setIsEditMode(true);
    setOpen(true);
  };

  const set = (key: string, val: any) => setEditing(p => ({ ...p!, [key]: val }));

  return (
    <AdminLayout title="Gift-страницы">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground text-sm">Управление премиальными подарочными страницами</p>
        <Button onClick={openCreate} size="sm">
          <Plus className="w-4 h-4 mr-1" />Создать
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Имя</TableHead>
              <TableHead>Событие</TableHead>
              <TableHead>Трек</TableHead>
              <TableHead>Стиль</TableHead>
              <TableHead>Просмотры</TableHead>
              <TableHead>Дата</TableHead>
              <TableHead className="w-40">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Загрузка...</TableCell></TableRow>
            ) : gifts.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Нет gift-страниц</TableCell></TableRow>
            ) : gifts.map((g) => (
              <TableRow key={g.id}>
                <TableCell className="font-medium">{g.hero_name}</TableCell>
                <TableCell>{g.event_name}</TableCell>
                <TableCell className="max-w-[180px] truncate">{g.track_title}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${g.gift_style === "black" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700"}`}>
                    {g.gift_style === "black" ? "Black" : "Light"}
                  </span>
                </TableCell>
                <TableCell className="text-center">{g.views_count}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{new Date(g.created_at).toLocaleDateString("ru")}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(g)} title="Редактировать">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPreviewSlug(g.slug)} title="Предпросмотр">
                      <Eye className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyLink(g.slug, g.id)} title="Копировать ссылку">
                      {copiedId === g.id ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </Button>
                    <a href={`/g/${g.slug}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="icon" className="h-8 w-8"><ExternalLink className="w-3 h-3" /></Button>
                    </a>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => { if (confirm("Удалить gift-страницу?")) remove.mutate(g.id); }}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? "Редактировать Gift-страницу" : "Новая Gift-страница"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Slug (URL)</Label><Input placeholder="ivan-birthday" value={editing?.slug || ""} onChange={e => set("slug", e.target.value)} /></div>
            <div><Label>Имя получателя *</Label><Input placeholder="ИВАН" value={editing?.hero_name || ""} onChange={e => set("hero_name", e.target.value)} /></div>
            <div><Label>Событие</Label><Input placeholder="ко Дню рождения" value={editing?.event_name || ""} onChange={e => set("event_name", e.target.value)} /></div>

            {/* Generation picker */}
            <div>
              <Label className="mb-2 block">Трек из генерации</Label>
              <GenerationPicker
                selectedId={(editing as any)?._generation_id}
                onSelect={(gen, audioUrl) => {
                  const userName = gen.telegram_users?.first_name || gen.telegram_users?.username || "";
                  const eventName = gen.events?.name || "";
                  setEditing(p => ({
                    ...p!,
                    track_title: `Песня для ${userName}`,
                    audio_url: audioUrl,
                    user_id: gen.user_id,
                    hero_name: p?.hero_name || userName.toUpperCase(),
                    event_name: p?.event_name || (eventName ? `ко дню ${eventName.toLowerCase()}` : ""),
                    _generation_id: gen.id,
                  } as any));
                }}
              />
            </div>

            <div><Label>Название трека</Label><Input placeholder="«Песня для Ивана»" value={editing?.track_title || ""} onChange={e => set("track_title", e.target.value)} /></div>
            <div><Label>URL аудио (mp3)</Label><Input placeholder="https://..." value={editing?.audio_url || ""} onChange={e => set("audio_url", e.target.value)} /></div>
            <div><Label>URL обложки (опц.)</Label><Input placeholder="https://..." value={editing?.cover_url || ""} onChange={e => set("cover_url", e.target.value)} /></div>
            <div><Label>User ID</Label><Input placeholder="user-id" value={editing?.user_id || ""} onChange={e => set("user_id", e.target.value)} /></div>

            {/* Sender info */}
            <div><Label>Имя дарителя (опц.)</Label><Input placeholder="Анна" value={editing?.sender_name || ""} onChange={e => set("sender_name", e.target.value)} /></div>
            <div>
              <Label>Сообщение от дарителя (до 300 символов)</Label>
              <Textarea
                placeholder="С Днём рождения! Этот хит — для тебя 🎶"
                value={editing?.custom_message || ""}
                onChange={e => set("custom_message", e.target.value.slice(0, 300))}
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">{(editing?.custom_message || "").length}/300</p>
            </div>

            {/* Style selector */}
            <div>
              <Label>Стиль страницы</Label>
              <div className="flex gap-3 mt-2">
                {(["light", "black"] as const).map(s => (
                  <button
                    key={s}
                    className="flex-1 rounded-xl p-3 border-2 transition-all text-center text-sm font-medium"
                    style={{
                      background: s === "black" ? "#0B0B0F" : "#F5F5F7",
                      color: s === "black" ? "#fff" : "#111",
                      borderColor: editing?.gift_style === s ? "#7F5AF0" : "transparent",
                    }}
                    onClick={() => set("gift_style", s)}
                  >
                    {s === "light" ? "☀️ Light" : "🖤 Black"}
                  </button>
                ))}
              </div>
            </div>

            {/* Accent color */}
            <div>
              <Label>Акцентный цвет</Label>
              <div className="flex gap-2 mt-2">
                {ACCENT_PRESETS.map(c => (
                  <button
                    key={c}
                    className="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110"
                    style={{
                      background: c,
                      borderColor: editing?.accent_color === c ? "#111" : "transparent",
                    }}
                    onClick={() => set("accent_color", c)}
                  />
                ))}
              </div>
            </div>

            {/* Animation toggle */}
            <div className="flex items-center justify-between">
              <Label>Анимация открытия</Label>
              <Switch
                checked={editing?.is_animation_enabled !== false}
                onCheckedChange={v => set("is_animation_enabled", v)}
              />
            </div>

            <Button className="w-full" onClick={() => editing && save.mutate(editing)} disabled={save.isPending}>
              {save.isPending
                ? (isEditMode ? "Сохранение..." : "Создание...")
                : (isEditMode ? "Сохранить" : "Создать")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewSlug} onOpenChange={() => setPreviewSlug(null)}>
        <DialogContent className="max-w-[480px] h-[85vh] p-0 overflow-hidden">
          <iframe
            src={`/g/${previewSlug}`}
            className="w-full h-full border-0"
            title="Gift Preview"
          />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
